import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { CoreModule } from '@rd/core/core.module';
import { UserCustomizationsFacade } from '@rd/core/facades/user-customizations-facade/user-customizations.facade';
import { EnvService } from '@rd/core/services/env/env.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { SharedModule } from '@rd/shared-legacy/shared.module';
import { CampaignBanner, UserCustomizationsModel } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { filter, Observable, switchMap, take, tap } from 'rxjs';
import {
  BNZ_BANKWIDE_CAROUSEL_UPDATE_ENABLED,
  BNZ_BANKWIDE_UPDATES_ENABLED,
} from '../../../core/tokens/features.tokens';
import { BnzButtonModule } from '../../../shared/components/bnz-button/button.module';
import { BnzBankWideIntroductionDialogComponent } from '../introduction-dialog/introduction-dialog.component';

@Component({
  standalone: true,
  selector: 'bnz-bankwide-homepage-banner',
  template: `
    <div
      *ngIf="bannerContent$ | async as bannerContent"
      class="mt-8 flex w-full flex-col items-center justify-center overflow-hidden rounded-md border border-solid border-[#d0d0d0] lg:mt-12 lg:flex-row">
      <div
        [ngClass]="[
          'flex h-[239px] w-full items-center justify-center overflow-hidden lg:max-w-[256px]',
          !isBankwideCarouselUpdateEnabled ? 'lg:h-[178px]' : 'lg:h-fit'
        ]">
        <img
          [ngClass]="[
            'hidden lg:block',
            !isBankwideCarouselUpdateEnabled ? 'h-[180px]' : 'h-[214px]'
          ]"
          [src]="bannerContent.desktopImageUrl"
          alt="" />
        <img
          class="h-[239px] w-full object-cover lg:hidden"
          [src]="bannerContent.mobileImageUrl"
          alt="" />
      </div>

      <div class="flex w-full flex-col p-4 text-[#002f6b]">
        <h2 class="m-0 mb-2 leading-[30px]">
          {{ bannerContent.title }}
        </h2>
        <p
          *ngIf="!isBankwideCarouselUpdateEnabled; else dynamicContent"
          class="m-0 mb-3 lg:mb-5 lg:line-clamp-2"
          [innerHTML]="bannerContent.mainContent"></p>

        <ng-template #dynamicContent>
          <div [innerHTML]="bannerContent.mainContent"></div>
        </ng-template>

        <button
          *ngIf="!isBnzBankwideUpdatesEnabled; else bnzBankwideUpdatesLink"
          bnz-button
          rdType="primary"
          class="min-h-[40px] w-[117px] py-2"
          (click)="onViewMoreClicked()">
          {{ 'wl.bnz.home_page.bank_wide.learn_more' | translate }}
        </button>

        <ng-template #bnzBankwideUpdatesLink>
          <a
            [href]="bannerContent.link"
            class="link focus-link flex min-h-[40px] w-[107px] items-center justify-center py-2 font-bold">
            {{ bannerContent.subContent }}
          </a>
        </ng-template>
      </div>
    </div>
  `,
  imports: [
    BnzButtonModule,
    CoreModule,
    CommonModule,
    SharedModule,
    TranslateModule,
    BnzBankWideIntroductionDialogComponent,
  ],
  styleUrls: ['./homepage-banner.component.scss'],
})
export class BnzBankWideHomepageBannerComponent
  implements OnInit, AfterViewInit
{
  private envService = inject(EnvService);
  private cmsFacade = inject(CmsFacade);
  private dialogService = inject(DialogService);
  private userCustomizationsFacade = inject(UserCustomizationsFacade);

  isBankwideCarouselUpdateEnabled = inject(
    BNZ_BANKWIDE_CAROUSEL_UPDATE_ENABLED,
  );
  isBnzBankwideUpdatesEnabled = inject(BNZ_BANKWIDE_UPDATES_ENABLED);

  userCustomizationsLoading$: Observable<boolean> =
    this.userCustomizationsFacade.userCustomizationsLoading$.pipe(
      filter(isNotNull),
    );

  userCustomizations$: Observable<UserCustomizationsModel> =
    this.userCustomizationsLoading$.pipe(
      filter((isLoading: boolean) => !isLoading),
      switchMap(() =>
        this.userCustomizationsFacade.userCustomizations$.pipe(
          filter(isNotNull),
        ),
      ),
    );

  bannerContent$: Observable<CampaignBanner> = this.cmsFacade
    .getContentByName({
      pageName: 'bnz--bankwide-homepage-banner',
      pageType: 'campaignbanner',
      locale: 'en-AU',
    })
    .pipe(
      filter(isNotNull),
      filter(
        (content): content is CampaignBanner =>
          content.type === 'campaignbanner',
      ),
      filter((banner) =>
        this.envService.isProduction ? banner.showBanner : banner.previewBanner,
      ),
    );

  overrideServerFlag =
    this.envService.shouldOverrideServerFlag('bankwide_dialog');

  onViewMoreClicked() {
    this.dialogService.open(BnzBankWideIntroductionDialogComponent, {
      id: 'bnz-bankwide-introduction-dialog',
      screenMode: 'center-md',
    });
  }

  ngOnInit(): void {
    this.userCustomizationsFacade.getUserCustomizations();
  }

  ngAfterViewInit(): void {
    if (!this.isBnzBankwideUpdatesEnabled) {
      this.userCustomizations$
        .pipe(
          take(1),
          tap(({ bankWideLoyaltyPopupDisplayed }: UserCustomizationsModel) => {
            if (!bankWideLoyaltyPopupDisplayed || this.overrideServerFlag) {
              this.dialogService.open(BnzBankWideIntroductionDialogComponent, {
                id: 'bnz-bankwide-introduction-dialog',
                screenMode: 'center-md',
                keepModalOnOverlayClick: true,
                keepModalOnEscapeKey: true,
              });
            }
          }),
        )
        .subscribe();
    }
  }
}
